import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

import { DataBagService } from '@/common/data-bag.service';
import { Data2Service } from '@/common/data2.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { DateService } from '@/common/date.service';

import { AccommodationsService } from './accommodations.service';
import { AccommodationsApiService } from './accommodations.api.service.ts.service';
import { filter, finalize, forkJoin } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class AccommodationsDataService {
	constructor(
		private readonly accommodationService: AccommodationsService,
		private readonly dateService: DateService,
		private readonly erp: Data2Service,
		private readonly bag: DataBagService,
		private readonly infobox: InfoboxService,
		private readonly loader: LoaderService,
		private readonly accommodationsApiService: AccommodationsApiService,
	) {}

	loadProductsAll(): Promise<void> {
		return new Promise((resolve) => {
			this.bag.get(`/product/?limit=999999&product_kind__id__in=1,2,5,6,7,8,9`, (result, error) => {
				if (error) {
					return;
				}
				this.accommodationService.data.products = result.results;
				resolve();
			});
		});
	}

	loadProducts(): Promise<void> {
		return new Promise((resolve) => {
			const store = this.accommodationService.selected.getValue();
			if (!store.destination.id) {
				return;
			}
			const locationIn = `&location__id__in=${store.destination.id}`;
			this.bag.get(`/product/?limit=200&product_kind__id__in=1,5${locationIn}`, (result, error) => {
				if (error) {
					return;
				}
				this.accommodationService.data.products = result.results;
				resolve();
			});
		});
	}

	loadNoname(): Promise<void> {
		this.accommodationService.data.nonames = [];
		if (this.accommodationService.selected.getValue().destination.id === 11) {
			return new Promise((resolve) => {
				environment.nonameIdsHR.forEach((element) => {
					this.bag.get(`/product/${element}/`, (result, error) => {
						if (error) {
							// return;
						}
						this.accommodationService.data.nonames.push(result);
						resolve();
					});
				});
			});
		}
		if (this.accommodationService.selected.getValue().destination.id === 12) {
			return new Promise((resolve) => {
				environment.nonameIdsMO.forEach((element) => {
					this.bag.get(`/product/${element}/`, (result, error) => {
						if (error) {
							// return;
						}
						this.accommodationService.data.nonames.push(result);
						resolve();
					});
				});
			});
		}
		return new Promise((resolve) => {
			environment.nonameIdsHR.forEach((element) => {
				this.bag.get(`/product/${element}/`, (result, error) => {
					if (error) {
						// return;
					}
					this.accommodationService.data.nonames.push(result);
					resolve();
				});
			});
		});
	}

	loadRoomTypes(): Promise<void> {
		return new Promise((resolve) => {
			this.bag.get('/product/room/type/?limit=200', (result) => {
				this.accommodationService.data.roomTypes = result.results;
				resolve();
			});
		});
	}

	loadLocations(): Promise<void> {
		return new Promise((resolve) => {
			this.bag.get('/location/basic/?limit=200&location_kind=CR', (result) => {
				this.accommodationService.data.destinations = result.results;
				resolve();
			});
		});
	}

	loadAccomodations(): void {
		this.loader.show();
		const productsIn = this.accommodationService.selected.getValue().products.join(',');
		this.bag.get(`/product/accommodation_file/?limit=200&is_active=true&product=${productsIn}`, (result) => {
			const accomodationFile = result.results;
			this.accommodationService.parseAccomodations(accomodationFile);
			this.loader.hide();
			this.loadReservationWithOutRoom();
			this.loadOccupancy();
			this.loadAvailabilities();
		});
	}

	loadAvailabilities(): void {
		if (this.accommodationService.data.productsSelected.length === 0) {
			return;
		}

		this.loader.show();

		const dateFrom = this.accommodationService.selected.getValue().date;
		const dateTo = this.dateService.daysAdd(60, dateFrom);
		const params = `lower=${dateFrom}&upper=${dateTo}/?limit=999999`;
		const productsIn = this.accommodationService.selected.getValue().products.join(',');

		forkJoin([
			this.accommodationsApiService.productRoomAvailability(params),
			this.accommodationsApiService.productRoomSlaveAvailability(params),
			this.accommodationsApiService.productRoomAvailabilityState(params, productsIn),
		])
			.pipe(
				filter((result) => !!result[0] && !!result[1] && !!result[2]),
				finalize(() => this.loader.hide()),
			)
			.subscribe(
				([
					productRoomAvailabilityResult,
					productRoomSlaveAvailabilityResult,
					productRoomAvailabilityStateResult,
				]) => {
					this.accommodationService.cleanAvailabilities();
					this.accommodationService.parseAvailabilities(
						productRoomAvailabilityResult.results,
						'room',
						'availability',
					);
					this.accommodationService.parseAvailabilities(
						productRoomSlaveAvailabilityResult.results,
						'roomSlave',
						'availability',
					);
					this.accommodationService.parseAvailabilities(
						productRoomAvailabilityStateResult.results,
						'room',
						'state',
					);
				},
			);
	}

	loadAvailabilitiesNoname(): void {
		const dateFrom = this.accommodationService.selected.getValue().date;
		const dateTo = this.dateService.daysAdd(60, dateFrom);
		const parms = 'lower=' + dateFrom + '&upper=' + dateTo + '/?limit=999999';
		this.loader.show('loadAvailabilitiesNoname');

		const productsInNoname = environment.nonameIdsHR.join(',');
		this.bag.get(`/product/room/availability_state/${parms}&product=${productsInNoname}`, (result, error) => {
			this.accommodationService.parseAvailabilities(result.results, 'room', 'noname');
			this.loader.hide('loadAvailabilitiesNoname');
		});
	}

	loadStateDetails(id: number): Promise<any> {
		return new Promise((resolve) => {
			this.loader.show('loadStateDetails');
			this.erp.command(`/Bag/BagIstourAccomodation/`, { cmd: 'roomDetail', state: id }, (result, error) => {
				if (error) {
					return;
				}
				this.loader.hide('loadStateDetails');
				resolve(result);
			});
		});
	}

	loadOccupancy(): void {
		this.loader.show('loadOccupancy');
		const productsIn = this.accommodationService.selected.getValue().products;
		this.erp.get('/Bag/BagAccomodations/?products=' + productsIn, (result, error) => {
			if (error) {
				const occupancy = false;
			}
			result.data.forEach((element) => {
				element.id = parseInt(element.id);
				if (!element.rooms) {
					return;
				}
				element.rooms.forEach((element2) => {
					element2.roomId = parseInt(element2.roomId);
				});
			});
			this.accommodationService.data.occupancy = result.data;
			this.accommodationService.parseOccupancy();
		});
	}

	loadReservationWithOutRoom(): void {
		this.accommodationService.data.withoutRooms = [];
		const productsIn = this.accommodationService.selected.getValue().products.join(',');
		let products = productsIn;
		if (this.accommodationService.selected.getValue().destination.id === 11) {
			products = environment.nonameIdsHR + ',' + productsIn;
		}
		if (this.accommodationService.selected.getValue().destination.id === 12) {
			products = environment.nonameIdsMO + ',' + productsIn;
		}
		const dateFrom = this.accommodationService.selected.getValue().date;
		const dateTo = this.dateService.daysAdd(60, dateFrom);

		this.erp.command(
			`/Bag/BagIstourAccomodation/`,
			{
				cmd: 'withOut',
				products: products,
				date_range: `${dateFrom},${dateTo}`,
			},
			(result, error) => {
				if (error) {
					return;
				}
				this.accommodationService.data.withoutRooms = result.data.map((obj) => {
					obj.status = obj.status == 6 ? 'OK' : 'OP';
					obj.roomGuarantee = obj.roomGuarantee != '' ? obj.roomGuarantee : '';
					obj.viewGuarantee = obj.viewGuarantee != 0 ? 'TAK' : '';
					obj.guarantee = obj.roomGuarantee;
					return obj;
				});
			},
		);
		const params = `?date_range=${dateFrom},${dateTo}&product=${products}`;

		this.bag.get(`/reservation/offer/without_room_state/${params}`, (result, error) => {
			if (error) {
				return;
			}
			this.accommodationService.data.withoutRooms = this.accommodationService.data.withoutRooms.concat(
				result.map((obj) => {
					obj.bookProductId = obj.product.id;
					obj.istourName = obj.product.name;
					obj.businessCaseNumber = obj.reservation;
					obj.dateFrom = obj.term.dateRange.lower;
					obj.dateTo = obj.term.dateRange.upper;
					obj.code = obj.roomType ? obj.roomType.code : '';
					obj.countClient = obj.slot.length;
					obj.orderRoom = 1;
					obj.countRoom = 1;
					obj.viewGuarantee = obj.viewGuarantee ? 'TAK' : '';
					obj.guarantee = obj.roomGuarantee ? 'Pokój' : '';
					obj.isOfferFromBAG = true;
					return obj;
				}),
			);
		});
	}
}
